import React, { useState, useMemo } from 'react';
import { Button } from './button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './select';
import { Input } from './input';
import { MultiSelect } from './multi-select';
import { Checkbox } from './checkbox';
import { Label } from './label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { Card, CardContent } from './card';
import { Separator } from './separator';
import { SearchableSelect } from './searchable-select';

export type LogicOperator = 'is' | 'is not' | 'is blank' | 'is not blank';
export type LogicJoin = 'AND' | 'OR';

export interface LogicRule {
  field: string;
  operator: LogicOperator;
  value?: string | string[] | null;
}

export interface LogicGroup {
  id: string;
  join: LogicJoin;
  rules: LogicRule[];
}

export interface ChecklistFieldOption {
  value: string;
  label: string;
}

export interface ChecklistField {
  id: string | number;
  name: string;
  type: string;
  path?: string; // Full path to field (e.g., "Live Install → Ground Mount → 6. Battery")
  options?: ChecklistFieldOption[];
}

export interface ConditionalLogicBuilderProps {
  fields: ChecklistField[];
  value: LogicGroup[];
  onChange: (val: LogicGroup[]) => void;
}

const getOperatorsForField = (fieldType: string): LogicOperator[] => {
  switch (fieldType) {
    case 'CHECKBOX':
      return ['is', 'is not'];
    case 'RADIOBUTTON':
    case 'MULTISELECT':
      return ['is', 'is not', 'is blank', 'is not blank'];
    default:
      return ['is', 'is not', 'is blank', 'is not blank'];
  }
};

export const ConditionalLogicBuilder: React.FC<ConditionalLogicBuilderProps> = ({ fields, value, onChange }) => {
  const [selectedField, setSelectedField] = useState<string>('');
  

  
  // Deduplicate fields to prevent duplicate key warnings
  const deduplicatedFields = fields.filter((field, index, self) => {
    const firstIndex = self.findIndex(f => f.id === field.id);
    return firstIndex === index;
  });

  const handleAddGroup = () => {
    const newGroup: LogicGroup = { id: Date.now().toString(), join: 'AND' as LogicJoin, rules: [] };
    const newValue = [...(value || []), newGroup];
    onChange(newValue);
  };

  const handleRemoveGroup = (groupId: string) => {
    onChange((value || []).filter(g => g.id !== groupId));
  };

  const handleGroupJoinChange = (groupId: string, join: LogicJoin) => {
    onChange((value || []).map(g => g.id === groupId ? { ...g, join } : g));
  };

  const handleAddRule = (groupId: string) => {
    onChange((value || []).map(g => 
      g.id === groupId 
        ? { ...g, rules: [...g.rules, { field: '', operator: 'is', value: '' }] }
        : g
    ));
  };

  const handleRemoveRule = (groupId: string, ruleIndex: number) => {
    onChange((value || []).map(g => 
      g.id === groupId 
        ? { ...g, rules: g.rules.filter((_, i) => i !== ruleIndex) }
        : g
    ));
  };

  const handleRuleChange = (groupId: string, ruleIndex: number, rule: Partial<LogicRule>) => {
    onChange((value || []).map(g => 
      g.id === groupId 
        ? { 
            ...g, 
            rules: g.rules.map((r, i) => i === ruleIndex ? { ...r, ...rule } : r)
          }
        : g
    ));
  };

  return (
    <div className="space-y-4">
      {(value || []).map((group, groupIndex) => (
        <Card key={group.id} className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">Rule Group {groupIndex + 1}</h4>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-destructive" 
                onClick={() => handleRemoveGroup(group.id)}
              >
                Remove Group
              </Button>
            </div>
            
            <Tabs value={group.join} onValueChange={(val) => handleGroupJoinChange(group.id, val as LogicJoin)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="AND">AND</TabsTrigger>
                <TabsTrigger value="OR">OR</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="mt-4 space-y-3">
              {group.rules.map((rule, ruleIndex) => {
                const field = deduplicatedFields.find(f => String(f.id) === String(rule.field));
                const operators = field ? getOperatorsForField(field.type) : [];
                


                
                return (
                  <div key={ruleIndex} className="border p-3 rounded-md bg-gray-50 overflow-hidden">
                    <div className="space-y-2">
                      {/* Field Row */}
                      <div className="w-full">
                        <Label className="text-xs">Field</Label>
                        <SearchableSelect
                          value={rule.field}
                          onValueChange={val => handleRuleChange(group.id, ruleIndex, { field: val, value: '' })}
                          inDialog={true}
                          options={deduplicatedFields.map(f => ({
                            label: f.name,
                            value: String(f.id),
                            description: f.path // Add path as helper text
                          }))}
                          placeholder="Search field..."
                          className="w-full"
                        />
                      </div>
                      
                      {/* Operator and Value Row */}
                      {(field || rule.field) && (
                        <div className="flex gap-2">
                          <div className="w-28">
                            <Label className="text-xs">Operator</Label>
                            <Select 
                              value={rule.operator} 
                              onValueChange={val => handleRuleChange(group.id, ruleIndex, { operator: val as LogicOperator, value: '' })}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="..." />
                              </SelectTrigger>
                              <SelectContent>
                                {(operators.length > 0 ? operators : ['is', 'is not', 'is blank', 'is not blank']).map(op => (
                                  <SelectItem key={op} value={op}>{op}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {rule.operator !== 'is blank' && rule.operator !== 'is not blank' && (
                            <div className="flex-1">
                              <Label className="text-xs">Value</Label>
                              {field && (field.type === 'RADIOBUTTON' || field.type === 'MULTISELECT') ? (
                                <Select 
                                  value={typeof rule.value === 'string' ? rule.value : ''} 
                                  onValueChange={val => handleRuleChange(group.id, ruleIndex, { value: val })}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options?.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : field && field.type === 'CHECKBOX' ? (
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    checked={rule.value === 'true'}
                                    onCheckedChange={checked => handleRuleChange(group.id, ruleIndex, { value: checked ? 'true' : 'false' })}
                                  />
                                  <Label className="text-xs">Checked</Label>
                                </div>
                              ) : (
                                <Input
                                  value={typeof rule.value === 'string' ? rule.value : ''}
                                  onChange={e => handleRuleChange(group.id, ruleIndex, { value: e.target.value })}
                                  placeholder="Enter value..."
                                />
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-end">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 text-xs" 
                              onClick={() => handleRemoveRule(group.id, ruleIndex)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <Button variant="outline" onClick={() => handleAddRule(group.id)}>
                Add Rule
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      
      <Button variant="outline" onClick={handleAddGroup}>
        Add Rule Group
      </Button>
    </div>
  );
}; 